/**
 * Hook for fetching nearby spots from user's Mekke and favorites
 */
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import { ApiError, type NearbySpotsResponseDto } from "@tabipla/shared";

interface UseNearbySpotsOptions {
  latitude?: number | null;
  longitude?: number | null;
  radiusKm?: number;
  limit?: number;
  enabled?: boolean;
}

/**
 * Fetch nearby spots from user's Mekke spots and favorites
 */
export function useNearbySpots({
  latitude,
  longitude,
  radiusKm = 5,
  limit = 10,
  enabled = true,
}: UseNearbySpotsOptions) {
  return useQuery({
    queryKey: ["nearby-spots", latitude, longitude, radiusKm, limit],
    queryFn: async () => {
      if (latitude == null || longitude == null) {
        return { mekkeSpots: [], favoriteSpots: [] };
      }

      const params = new URLSearchParams({
        latitude: latitude.toString(),
        longitude: longitude.toString(),
        radiusKm: radiusKm.toString(),
        limit: limit.toString(),
      });

      const response = await apiClient.get<NearbySpotsResponseDto>(
        `/api/integration/nearby-spots?${params.toString()}`
      );

      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch nearby spots");
      }

      return response.data ?? { mekkeSpots: [], favoriteSpots: [] };
    },
    enabled: enabled && latitude != null && longitude != null,
    staleTime: 60000, // Cache for 1 minute
  });
}

/**
 * Check if there are any nearby spots
 */
export function hasNearbySpots(data: NearbySpotsResponseDto | null | undefined): boolean {
  if (!data) return false;
  return data.mekkeSpots.length > 0 || data.favoriteSpots.length > 0;
}
